import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Select,
  MenuItem,
  TextareaAutosize,
  Box,
} from '@mui/material';
import {
  useFetchAllPhase,
  useFetchAllStage,
  useFetchPartnerServiceCreation,
  useFetchServiceCreationActivity,
} from '../../hooks/mssp-api-hooks';
import { HbrInput, HbrOption, HbrSelect } from '@harbor/elements-react-standalone';
import { ServiceCreation } from '../../types/msp';
import { postItem, putItem } from '../../support/queries';
import StbButton from '../security-toolbox-ui/StbButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../support/store';

// --- Activity Template ---
const allActivitiesTemplate = [
  { stage: 'Business Development (PRE LAUNCH)', phase: 'Service Creation', activity_name: 'Business Plan / Joint KPIs (Exec Sponsor)' },
  { stage: 'Business Development (PRE LAUNCH)', phase: 'Service Creation', activity_name: 'Identification of Market Segment / Route to Market' },
  { stage: 'Business Development (PRE LAUNCH)', phase: 'Service Creation', activity_name: 'Alignment with Internal Cisco stakeholders (Global, Regional,BU)' },
  { stage: 'Business Development (PRE LAUNCH)', phase: 'Service Creation', activity_name: 'MOU (Program Pricing/PIF/PAF)' },
  { stage: 'Business Development (PRE LAUNCH)', phase: 'Service Creation', activity_name: 'Use Cases Identification / Documentation' },
  { stage: 'Business Development (PRE LAUNCH)', phase: 'Service Creation', activity_name: 'Lab/s Requirements (Demo, PoC, Certification)' },
  { stage: 'Business Development (PRE LAUNCH)', phase: 'Service Creation', activity_name: 'Offer (Product) Requirements / Budget & Resources' },
  { stage: 'Business Development (PRE LAUNCH)', phase: 'Service Creation', activity_name: 'Establish Timelines' },
  { stage: 'Product Readiness', phase: 'Product Readiness', activity_name: 'Cost Model (All RTM\'s)' },
  { stage: 'Product Readiness', phase: 'Product Readiness', activity_name: 'Service Description (Incl. JVP)' },
  { stage: 'Product Readiness', phase: 'Product Readiness', activity_name: 'Launch Kit / Sales Collateral' },
  { stage: 'Product Readiness', phase: 'Product Readiness', activity_name: 'Marketing & Communications Plan' },
  { stage: 'Product Readiness', phase: 'Product Readiness', activity_name: 'Enablement Plan (Sales, Technical, Operations)' },
  { stage: 'Technology Readiness', phase: 'Technology Readiness', activity_name: 'Use Case Reference Architecture' },
  { stage: 'Technology Readiness', phase: 'Technology Readiness', activity_name: 'Lab Creation' },
  { stage: 'Technology Readiness', phase: 'Technology Readiness', activity_name: 'Tech testing & POC, Certification' },
  { stage: 'Technology Readiness', phase: 'Technology Readiness', activity_name: 'Systems Integration (API , BSS/OSS)' },
  { stage: 'Operational Readiness', phase: 'Operational Support Readiness', activity_name: 'Onboarding and Change Plan' },
  { stage: 'Operational Readiness', phase: 'Operational Support Readiness', activity_name: 'RunBook + Ops Guide Created' },
  { stage: 'Operational Readiness', phase: 'Operational Support Readiness', activity_name: 'Ops Training Plan' },
  { stage: 'Operational Readiness', phase: 'Operational Support Readiness', activity_name: 'System + Ops Testing' },
  { stage: 'Sales Readiness', phase: 'Sales Acceleration', activity_name: '90-Day Sales Execution Plan' },
  { stage: 'Sales Readiness', phase: 'Sales Acceleration', activity_name: 'Enablement Plan of Execution' },
  { stage: 'Sales Readiness', phase: 'Sales Acceleration', activity_name: 'Demand Gen Plan of Execution' },
  { stage: 'Sales Readiness', phase: 'Sales Acceleration', activity_name: 'Marketing/Comms Plan of Execution' },
  { stage: 'Sales Readiness', phase: 'Sales Acceleration', activity_name: 'QBR Cadence (Format and Stakeholders)' },
];

// --- Status Colors ---
const statusColors: Record<string, string> = {
  'Not Started': '#F0F1F2',
  'N/A': '#6BBF41',
  'In Progress': '#1D69CC',
  'Delayed': '#F0C243',
  'On-Going': '#F7782F',
  'Completed': '#6BBF41',
  'Blocked': '#FA5762',
};

// --- Inline Control Options ---
const OVERALL_STATUS_OPTIONS = [
  'No Activity', 'Qualifying', 'Standstill', 'Incubating',
  'Limited Access', 'Launched', 'N/A'
];
const CISCO_POWERED_OPTIONS = ['Yes', 'No', 'In progress'];
const DEFAULT_OVERALL_STATUS = 'No Activity';
const DEFAULT_CISCO_POWERED = 'No';

// Helper to convert ISO date to datetime-local format
const toDateLocal = (dateString: string | Date | undefined) => {
  if (!dateString) return '';
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  // Ensure valid date
  if (isNaN(date.getTime())) return '';
  const pad = (n: number) => (n < 10 ? '0' + n : n);
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate())
  );
};

interface ServiceCreationActivitiesTableProps {
  partnerId: number;
  productId: number;
}

const ServiceCreationActivitiesTable: React.FC<ServiceCreationActivitiesTableProps> = ({ partnerId, productId }) => {
  const allProducts = useSelector((state: RootState) => state.products.items);
  const product = allProducts.find(p => p.id === productId) || null;

  const [editingCommentIndex, setEditingCommentIndex] = useState<number | null>(null);
  const [commentDraft, setCommentDraft] = useState<string>('');
  const [serviceCreationActivities, setServiceCreationActivities] = useState<any[]>([]);
  const [serviceCreation, setServiceCreation] = useState<ServiceCreation | null>(null);

  // Inline controls state
  const [overallStatus, setOverallStatus] = useState(DEFAULT_OVERALL_STATUS);
  const [targetLaunchDate, setTargetLaunchDate] = useState('');
  const [ciscoPowered, setCiscoPowered] = useState(DEFAULT_CISCO_POWERED);

  // Fetch hooks
  const { data: partnerServiceCreation, isLoading: isLoadingPartnerServiceCreation } =
    useFetchPartnerServiceCreation(partnerId);
  const { data: serviceCreationActivity, refetch: refetchActivity, isLoading: isLoadingServiceCreationActivity } =
    useFetchServiceCreationActivity(serviceCreation?.id);
  const { data: stages } = useFetchAllStage();
  const { data: phases } = useFetchAllPhase();

  // Update serviceCreation and inline controls when partnerServiceCreation or productId changes
  useEffect(() => {
    if (partnerServiceCreation && productId) {
      const found = partnerServiceCreation.find((sc: any) => sc?.product?.id === productId);
      setServiceCreation(found || null);
      setOverallStatus(found?.status || DEFAULT_OVERALL_STATUS);
      setTargetLaunchDate(toDateLocal(found?.target_launch_date) || toDateLocal(new Date()));
      setCiscoPowered(found?.cisco_powered || DEFAULT_CISCO_POWERED);
    }
  }, [partnerServiceCreation, productId]);

  // Update activities when serviceCreation changes
  useEffect(() => {
    if (serviceCreation?.id) {
      refetchActivity();
    }
  }, [serviceCreation, refetchActivity]);

  // Map activities to the template when fetched
  useEffect(() => {
    if (!isLoadingServiceCreationActivity && serviceCreationActivity && serviceCreation) {
      const updatedActivities = allActivitiesTemplate.map((activityTemplate) => {
        const matchingActivity = serviceCreationActivity.find(
          (activity: any) =>
            activity.activity_name === activityTemplate.activity_name &&
            activity.service_creation === serviceCreation.id
        );
        return {
          ...activityTemplate,
          status: matchingActivity ? matchingActivity.status : 'Not Started',
          comments: matchingActivity ? matchingActivity.comments : '',
          owner: matchingActivity ? matchingActivity.owner : '',
          id: matchingActivity ? matchingActivity.id : null,
        };
      });
      setServiceCreationActivities(updatedActivities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceCreationActivity, serviceCreation]);

  // Inline Controls Immediate Save Handlers
  const handleInlineStatusChange= async (e: any) => {
    const value = e.target.value || DEFAULT_OVERALL_STATUS;
    setOverallStatus(value);
    if (serviceCreation?.id) {
      await putItem(`db/service-creation`, {
        id: serviceCreation.id,
        partner_id: serviceCreation.partner.id,
        product_id: serviceCreation.product.id,
        overall_status: serviceCreation.overall_status,
        status: value,
        target_launch_date: serviceCreation.target_launch_date,
        service_launch_date: serviceCreation.service_launch_date,
        cisco_powered: ciscoPowered,
        version_id: serviceCreation.version ? serviceCreation.version.id : null,
        license_id: serviceCreation.license ? serviceCreation.license.id : null,
        created: serviceCreation.created,
        last_updated: serviceCreation.last_updated,
      });
    }
  };

  const handleInlineDateChange = async (e: any) => {
    const value = e.target.value;
    setTargetLaunchDate(value);
    if (serviceCreation?.id) {
      // Ensure zeroed-out time
      let isoDate = null;
      if (value) {
        const [year, month, day] = value.split('-');
        const utcMidnight = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0));
        isoDate = utcMidnight.toISOString();
      }
      await putItem(`db/service-creation`, {
        id: serviceCreation.id,
        partner_id: serviceCreation.partner.id,
        product_id: serviceCreation.product.id,
        status: serviceCreation.status,
        overall_status: serviceCreation.overall_status,
        target_launch_date: isoDate,
        service_launch_date: serviceCreation.service_launch_date,
        cisco_powered: ciscoPowered,
        version_id: serviceCreation.version ? serviceCreation.version.id : null,
        license_id: serviceCreation.license ? serviceCreation.license.id : null,
        created: serviceCreation.created,
        last_updated: serviceCreation.last_updated,
      });
    }
  };

  const handleInlineCiscoPoweredChange = async (e) => {
    const value = e.target.value || DEFAULT_CISCO_POWERED;
    setCiscoPowered(value);
    if (serviceCreation?.id) {
      await putItem(`db/service-creation`, {
        id: serviceCreation.id,
        partner_id: serviceCreation.partner.id,
        product_id: serviceCreation.product.id,
        status: serviceCreation.status,
        overall_status: serviceCreation.overall_status,
        target_launch_date: serviceCreation.target_launch_date,
        service_launch_date: serviceCreation.service_launch_date,
        cisco_powered: value,
        version_id: serviceCreation.version ? serviceCreation.version.id : null,
        license_id: serviceCreation.license ? serviceCreation.license.id : null,
        created: serviceCreation.created,
        last_updated: serviceCreation.last_updated,
      });
    }
  };

  // --- Activity Table Logic ---
  const handleStatusChange = async (index: number, newStatus: string) => {
    const updatedActivities = [...serviceCreationActivities];
    updatedActivities[index] = { ...updatedActivities[index], status: newStatus };
    setServiceCreationActivities(updatedActivities);

    const activity = updatedActivities[index];
    const updates: any = {
      status: newStatus,
      activity_name: activity.activity_name,
      stage: stages.find((s: any) => s.name === activity.stage)?.id,
      phase: phases.find((p: any) => p.name === activity.phase)?.id,
      service_creation: serviceCreation?.id,
      ...(activity.id ? { id: activity.id } : {}),
    };

    try {
      await (activity.id
        ? putItem('db/service-creation-activity', updates)
        : postItem('db/service-creation-activity', updates));
    } catch (error) {
      alert('Failed to update the status. Please try again.');
      console.error(error);
    }
  };

  const handleCommentEditStart = (index: number) => {
    setEditingCommentIndex(index);
    setCommentDraft(serviceCreationActivities[index]?.comments || '');
  };

  const handleCommentSave = async (index: number) => {
    const updatedActivities = [...serviceCreationActivities];
    updatedActivities[index] = { ...updatedActivities[index], comments: commentDraft };
    setServiceCreationActivities(updatedActivities);

    const activity = updatedActivities[index];
    const updates: any = {
      comments: commentDraft,
      activity_name: activity.activity_name,
      stage: stages.find((s: any) => s.name === activity.stage)?.id,
      phase: phases.find((p: any) => p.name === activity.phase)?.id,
      service_creation: serviceCreation?.id,
      ...(activity.id ? { id: activity.id } : {}),
    };

    try {
      await (activity.id
        ? putItem('db/service-creation-activity', updates)
        : postItem('db/service-creation-activity', updates));
    } catch (error) {
      alert('Failed to save the comment. Please try again.');
      console.error(error);
    }

    setEditingCommentIndex(null);
  };

  const handleCommentCancel = () => setEditingCommentIndex(null);

  const renderTableRows = () => {
    if (isLoadingPartnerServiceCreation || !productId) {
      return (
        <TableRow>
          <TableCell colSpan={6}>Loading...</TableCell>
        </TableRow>
      );
    }

    return serviceCreationActivities.map((activity, index) => (
      <TableRow key={index}>
        <TableCell>{activity.stage}</TableCell>
        <TableCell>{activity.phase}</TableCell>
        <TableCell>{activity.owner}</TableCell>
        <TableCell>{activity.activity_name}</TableCell>
        <TableCell>
          {editingCommentIndex === index ? (
            <div>
              <TextareaAutosize
                minRows={3}
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                style={{ width: '100%' }}
              />
              <div>
                <StbButton iconOnly variant="text" role="save" onClick={() => handleCommentSave(index)} className="mr-2" />
                <StbButton iconOnly variant="text" role="cancel" onClick={handleCommentCancel} />
              </div>
            </div>
          ) : (
            <div className="flex flex-row items-start justify-content gap-2 relative">
              <div className="flex-1">{activity.comments}</div>
              <StbButton iconOnly variant="text" role="update" onClick={() => handleCommentEditStart(index)} />
            </div>
          )}
        </TableCell>
        <TableCell>
          <Select
            value={activity.status}
            onChange={(e) => handleStatusChange(index, e.target.value as string)}
            style={{ backgroundColor: statusColors[activity.status], width: '90%' }}
          >
            {Object.keys(statusColors).map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <TableContainer component={Paper}>
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2} pt={2}>
        <Typography variant="h6" component="div">
          Service Creation Activities - {product?.name || 'Loading...'}
        </Typography>
        {serviceCreation && (
          <Box display="flex" alignItems="center" gap={2}>
            <HbrSelect
              size="small"
              label="Overall Status"
              value={overallStatus ?  overallStatus : 'No Activity'}
              onHbr-change={handleInlineStatusChange}
              style={{ minWidth: 160 }}
            >
              {OVERALL_STATUS_OPTIONS.map(opt => (
                <HbrOption id={opt} key={opt} value={opt} >
                  {opt}
                </HbrOption>
              ))}
            </HbrSelect>

            <HbrInput
              name="target_launch_date"
              label="Target Launch Date"
              size="small"
              type="date"
              value={targetLaunchDate ? targetLaunchDate.substring(0, 10) : ''}
              onInput={handleInlineDateChange}
              required
              style={{ minWidth: 180 }}
            />

            <HbrSelect
              size="small"
              label="Cisco Powered"
              value={ciscoPowered || 'No'}
              onHbr-change={handleInlineCiscoPoweredChange}
              style={{ minWidth: 130 }}
            >
              {CISCO_POWERED_OPTIONS.map(opt => (
                <HbrOption id={opt} key={opt}  value={opt}>
                  {opt}
                </HbrOption>
              ))}
            </HbrSelect>
          </Box>
        )}
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Stage</TableCell>
            <TableCell>Phase</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell>Activity</TableCell>
            <TableCell>Comments</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderTableRows()}</TableBody>
      </Table>
    </TableContainer>
  );
};

export default ServiceCreationActivitiesTable;